import { Form, Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useCampaignSaveAndGenerate } from "../../hooks/useCampaignSaveAndGenerate";
import { useCampaignLoadQuery, useCampaignUpdateMutation } from "../../services/server/CampaignApi";
import type { CampaignEntity } from "../../types/CampaignEntity";
import campaignEditJson from "./campaign-terminals-edit.json";

export const CampaignTerminalsEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { id } = useParams();
    const [form] = Form.useForm();

    const formDefinition = campaignEditJson as FormJson;

    const [formData, setFormData] = useState<CampaignEntity>({} as CampaignEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useCampaignLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const { saveAndGenerate, isSaveInProgress } = useCampaignSaveAndGenerate(useCampaignUpdateMutation);
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.CAMPAIGNS}`);
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            // Get fresh values from form instance to capture CSV imports and other changes
            // that may not have propagated to formData due to React batching
            const freshValues = form.getFieldsValue();
            const dataToSave = { ...formData, ...freshValues };

            const result = await saveAndGenerate(dataToSave, formAction);

            const hasError = "error" in result;
            if (!hasError) {
                const successText =
                    formAction === FORM_ACTION.GENERATE
                        ? i18n.t("success.campaign.generate.successfully", { 0: result.data.id })
                        : i18n.t("common.formUpdateSuccess", { id: result.data.id, resource: "Campaign" });
                completeSubmit(successText);
            }
        },
        [saveAndGenerate, formData, form, formAction, completeSubmit],
    );

    return (
        <Spin spinning={isSaveInProgress || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<CampaignEntity>
                    name="CampaignTerminalsEditForm"
                    customFormInstance={form}
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
